import { BlobCtaButton, GlassIconButton, SelectField, useToast } from "@cupcodev/ui";
import { useEffect, useState } from "react";
import { SLOT_LENGTHS, type AvailabilityBlock, type AvailabilityRule } from "../../shared/meetings/meetings";
import { Hint } from "../../ui/Hint";
import { EmptyState, NaveIcon, Notice, Panel, SectionSkeleton } from "../../ui/NavePage";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";

/** Staff-only editor for the weekly "predefined days" and blocked dates that customers can book against. */
export const AgendaAvailabilityEditor = () => {
  const { runtime } = useScenario();
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [rules, setRules] = useState<readonly AvailabilityRule[]>([]);
  const [blocks, setBlocks] = useState<readonly AvailabilityBlock[]>([]);
  const [newDate, setNewDate] = useState("");
  const [newReason, setNewReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    if (!runtime.experience.getMeetingRules) { setState("error"); return; }
    void runtime.experience.getMeetingRules().then((result) => {
      if (cancelled) return;
      if (!result.ok) { setState("error"); return; }
      setRules(result.value.rules);
      setBlocks(result.value.blocks);
      setState("ready");
    }).catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [runtime]);

  const weekday = (index: number): string => new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2023, 0, 1 + index)));
  const update = (index: number, patch: Partial<AvailabilityRule>): void => setRules((current) => current.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  const addRule = (): void => setRules((current) => [...current, { weekday: 1, startTime: "09:00", endTime: "12:00", slotMinutes: 30, isActive: true }]);
  const addBlock = (): void => {
    if (!newDate || blocks.some((block) => block.date === newDate)) return;
    setBlocks((current) => [...current, { date: newDate, ...(newReason.trim() ? { reason: newReason.trim() } : {}) }].sort((a, b) => a.date.localeCompare(b.date)));
    setNewDate(""); setNewReason("");
  };

  const save = async (): Promise<void> => {
    if (busy || !runtime.experience.saveMeetingRules) return;
    setBusy(true); setError(undefined);
    try {
      const result = await runtime.experience.saveMeetingRules({ rules, blocks });
      if (!result.ok) { setError(result.error.code); return; }
      setRules(result.value.rules); setBlocks(result.value.blocks);
      toast({ variant: "success", title: t("agenda.admin.saved") });
    } catch { setError("DEPENDENCY_UNAVAILABLE"); } finally { setBusy(false); }
  };

  return (
    <Panel icon="lucide:calendar-cog" title={t("agenda.admin.title")}>
      <p className="nave-prose">{t("agenda.admin.description")}</p>
      {state === "loading" ? <SectionSkeleton label={t("state.loading")} rows={2} /> : state === "error" ? <Notice tone="warning">{t("agenda.unavailable")}</Notice> : <>
        {rules.length === 0 ? <EmptyState icon="lucide:calendar-x">{t("agenda.admin.empty")}</EmptyState> : <ul className="nave-agenda-rules">
          {rules.map((rule, index) => <li key={`${rule.weekday}-${index}`} className="nave-agenda-rule">
            <div className="nave-field"><span>{t("agenda.admin.weekday")}</span>
              <SelectField value={`${rule.weekday}`} ariaLabel={t("agenda.admin.weekday")} onChange={(value) => update(index, { weekday: Number(value) })} disabled={busy}
                options={Array.from({ length: 7 }, (_, day) => ({ value: `${day}`, label: weekday(day) }))} /></div>
            <label className="nave-field"><span>{t("agenda.admin.start")}</span><input type="time" value={rule.startTime} disabled={busy} onChange={(event) => update(index, { startTime: event.target.value })} /></label>
            <label className="nave-field"><span>{t("agenda.admin.end")}</span><input type="time" value={rule.endTime} disabled={busy} onChange={(event) => update(index, { endTime: event.target.value })} /></label>
            <div className="nave-field"><span>{t("agenda.admin.slot")}</span>
              <SelectField value={`${rule.slotMinutes}`} ariaLabel={t("agenda.admin.slot")} onChange={(value) => update(index, { slotMinutes: Number(value) })} disabled={busy}
                options={SLOT_LENGTHS.map((minutes) => ({ value: `${minutes}`, label: t("agenda.admin.slotMinutes", { minutes }) }))} /></div>
            <label className="nave-agenda-check"><input type="checkbox" checked={rule.isActive} disabled={busy} onChange={(event) => update(index, { isActive: event.target.checked })} /><span>{t("agenda.admin.active")}</span></label>
            <Hint label={t("agenda.admin.removeRule")}><GlassIconButton type="button" variant="destructive" size="sm" aria-label={t("agenda.admin.removeRule")} disabled={busy}
              onClick={() => setRules((current) => current.filter((_, i) => i !== index))}><NaveIcon name="lucide:trash-2" /></GlassIconButton></Hint>
          </li>)}
        </ul>}
        <div className="nave-form-actions"><BlobCtaButton type="button" size="sm" variant="secondary" disabled={busy} onClick={addRule}>{t("agenda.admin.addRule")}</BlobCtaButton></div>
        <h3 className="nave-agenda-subtitle">{t("agenda.admin.blocks")}</h3>
        <div className="nave-agenda-block-form">
          <label className="nave-field"><span>{t("agenda.admin.blockDate")}</span><input type="date" value={newDate} disabled={busy} onChange={(event) => setNewDate(event.target.value)} /></label>
          <label className="nave-field"><span>{t("agenda.admin.blockReason")}</span><input type="text" maxLength={160} value={newReason} disabled={busy} onChange={(event) => setNewReason(event.target.value)} /></label>
          <BlobCtaButton type="button" size="sm" variant="secondary" disabled={busy || !newDate} onClick={addBlock}>{t("agenda.admin.addBlock")}</BlobCtaButton>
        </div>
        {blocks.length ? <ul className="nave-agenda-blocks">{blocks.map((block) => <li key={block.date}>
          <span>{new Intl.DateTimeFormat(locale, { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${block.date}T00:00:00Z`))}{block.reason ? ` · ${block.reason}` : ""}</span>
          <Hint label={t("agenda.admin.removeBlock")}><GlassIconButton type="button" variant="destructive" size="sm" aria-label={t("agenda.admin.removeBlock")} disabled={busy}
            onClick={() => setBlocks((current) => current.filter((item) => item.date !== block.date))}><NaveIcon name="lucide:x" /></GlassIconButton></Hint>
        </li>)}</ul> : null}
        {error ? <Notice tone="warning">{t(error === "VALIDATION_ERROR" ? "agenda.error.validation" : error === "FORBIDDEN" ? "agenda.error.forbidden" : "agenda.error.unavailable")}</Notice> : null}
        <div className="nave-form-actions"><BlobCtaButton type="button" size="sm" loading={busy} disabled={busy} onClick={() => void save()}>{t("agenda.admin.save")}</BlobCtaButton></div>
      </>}
    </Panel>
  );
};
